import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
    Box,
    Button,
    CircularProgress,
    TextField,
    Typography,
    useTheme,
} from '@mui/material'
import { ArrowBack, Apartment, AttachFile, Send, Event } from '@mui/icons-material'

import { FlexDiv } from '../../components'
import { ResponsivePageContainer } from '../../components/PageContainers'
import { getLongFormDate } from '../../helpers/stringHelpers'
import { AppRoutes, resolveRoute } from '../../helpers/Routes'
import { useTenantMessages } from '../../hooks/useTenantMessages'
import { TenantMessage, Reply } from '../../types/tenantMessage'

const ReplyBubble: React.FC<{ reply: Reply }> = ({ reply }) => {
    const theme = useTheme()
    const isTenant = reply.sender_type === 'TENANT'

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: isTenant ? 'flex-end' : 'flex-start',
                marginBottom: theme.spacing(1.5),
            }}
        >
            <Box
                sx={{
                    maxWidth: '85%',
                    padding: theme.spacing(1.5, 2),
                    borderRadius: 2,
                    backgroundColor: isTenant
                        ? 'rgba(70, 176, 131, 0.15)'
                        : 'rgba(255, 255, 255, 0.06)',
                    border: isTenant
                        ? `1px solid ${theme.palette.primary.main}30`
                        : '1px solid rgba(255,255,255,0.08)',
                }}
            >
                <FlexDiv
                    style={{
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: theme.spacing(0.5),
                        gap: theme.spacing(2),
                    }}
                >
                    <Typography
                        variant="caption"
                        sx={{
                            color: isTenant
                                ? theme.palette.primary.main
                                : '#8b949e',
                            fontWeight: 600,
                        }}
                    >
                        {reply.sender_name}
                        {!isTenant && (
                            <span
                                style={{
                                    marginLeft: 6,
                                    fontSize: '0.65rem',
                                    opacity: 0.7,
                                }}
                            >
                                Staff
                            </span>
                        )}
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{ color: '#8b949e', fontSize: '0.65rem' }}
                    >
                        {getLongFormDate(reply.created_date)}
                    </Typography>
                </FlexDiv>
                <Typography
                    variant="body2"
                    sx={{
                        color: 'rgba(255, 255, 255, 0.85)',
                        lineHeight: 1.6,
                        whiteSpace: 'pre-wrap',
                    }}
                >
                    {reply.body}
                </Typography>
            </Box>
        </Box>
    )
}

export const MessageDetail = () => {
    const theme = useTheme()
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()

    const {
        isLoading,
        getMessage,
        markRead,
        replies,
        repliesLoading,
        getReplies,
        sendReply,
    } = useTenantMessages()

    const [message, setMessage] = useState<TenantMessage | null>(null)
    const [replyText, setReplyText] = useState('')
    const [isSending, setIsSending] = useState(false)

    useEffect(() => {
        if (!id) return

        const loadMessage = async () => {
            try {
                const msg = await getMessage(Number(id))
                setMessage(msg)
                if (!msg.is_read) {
                    await markRead(msg.id)
                    setMessage((prev) =>
                        prev ? { ...prev, is_read: true } : prev,
                    )
                }
                await getReplies(Number(id))
            } catch {
                // error already toasted by hook
            }
        }

        loadMessage()
    }, [id])

    const handleSendReply = async () => {
        if (!id || !replyText.trim() || isSending) return
        setIsSending(true)
        try {
            await sendReply(Number(id), replyText.trim())
            setReplyText('')
        } catch {
            // error already toasted by hook
        } finally {
            setIsSending(false)
        }
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            handleSendReply()
        }
    }

    return (
        <ResponsivePageContainer>
            <Box sx={{ maxWidth: '700px', width: '100%' }}>
                <Button
                    startIcon={<ArrowBack />}
                    onClick={() => navigate(AppRoutes.messages)}
                    sx={{
                        color: '#8b949e',
                        textTransform: 'none',
                        marginBottom: theme.spacing(2),
                        '&:hover': {
                            color: '#fff',
                        },
                    }}
                >
                    Back to Messages
                </Button>

                {isLoading ? (
                    <FlexDiv
                        style={{
                            justifyContent: 'center',
                            padding: theme.spacing(4),
                            width: '100%',
                        }}
                    >
                        <CircularProgress
                            size={40}
                            style={{ color: theme.palette.primary.main }}
                        />
                    </FlexDiv>
                ) : message ? (
                    <>
                        {/* Message header card */}
                        <Box
                            sx={{
                                background:
                                    'linear-gradient(135deg, #2a2f35 0%, #1e2328 100%)',
                                borderRadius: 3,
                                padding: 3,
                                border: `1px solid ${theme.palette.grey[700]}`,
                                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                                position: 'relative',
                                overflow: 'hidden',
                            }}
                        >
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    height: 4,
                                    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, #3b82f6 100%)`,
                                }}
                            />

                            <Typography
                                variant="h5"
                                sx={{
                                    color: '#fff',
                                    fontWeight: 700,
                                    marginBottom: theme.spacing(2),
                                }}
                            >
                                {message.subject}
                            </Typography>

                            <FlexDiv
                                style={{
                                    alignItems: 'center',
                                    marginBottom: theme.spacing(0.5),
                                }}
                            >
                                <Apartment
                                    sx={{
                                        color: '#8b949e',
                                        fontSize: 18,
                                        marginRight: theme.spacing(0.5),
                                    }}
                                />
                                <Typography
                                    variant="body2"
                                    sx={{ color: '#8b949e' }}
                                >
                                    {message.initiated_by_tenant
                                        ? message.apartment_name
                                        : `From: ${message.apartment_name}`}
                                </Typography>
                            </FlexDiv>

                            {message.initiated_by_tenant && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: theme.palette.primary.main,
                                        fontStyle: 'italic',
                                        marginBottom: theme.spacing(0.5),
                                        display: 'block',
                                    }}
                                >
                                    You started this conversation
                                </Typography>
                            )}

                            <Typography
                                variant="body2"
                                sx={{
                                    color: '#8b949e',
                                    marginBottom: message.property_event_id
                                        ? theme.spacing(1.5)
                                        : theme.spacing(3),
                                }}
                            >
                                {getLongFormDate(message.created_date)}
                            </Typography>

                            {message.property_event_id && (
                                <Button
                                    startIcon={<Event />}
                                    size="small"
                                    onClick={() =>
                                        navigate(
                                            resolveRoute(
                                                AppRoutes.eventDetail,
                                                ':id',
                                                message.property_event_id!,
                                            ),
                                        )
                                    }
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        color: theme.palette.primary.main,
                                        marginBottom: theme.spacing(2),
                                        borderRadius: 2,
                                        backgroundColor:
                                            'rgba(70, 176, 131, 0.08)',
                                        '&:hover': {
                                            backgroundColor:
                                                'rgba(70, 176, 131, 0.16)',
                                        },
                                    }}
                                >
                                    View Community Event
                                </Button>
                            )}

                            <Box
                                sx={{
                                    borderTop:
                                        '1px solid rgba(255,255,255,0.1)',
                                    paddingTop: theme.spacing(3),
                                }}
                            >
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: 'rgba(255, 255, 255, 0.85)',
                                        lineHeight: 1.8,
                                        whiteSpace: 'pre-wrap',
                                    }}
                                >
                                    {message.body}
                                </Typography>
                            </Box>

                            {message.attachments.length > 0 && (
                                <Box
                                    sx={{
                                        marginTop: theme.spacing(3),
                                        borderTop:
                                            '1px solid rgba(255,255,255,0.1)',
                                        paddingTop: theme.spacing(2),
                                    }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            color: '#8b949e',
                                            marginBottom: theme.spacing(1),
                                        }}
                                    >
                                        Attachments
                                    </Typography>
                                    {message.attachments.map((attachment) => (
                                        <Button
                                            key={attachment.id}
                                            startIcon={<AttachFile />}
                                            href={attachment.file_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{
                                                color: theme.palette.primary
                                                    .main,
                                                textTransform: 'none',
                                                fontWeight: 500,
                                                justifyContent: 'flex-start',
                                                display: 'flex',
                                                marginBottom: theme.spacing(
                                                    0.5,
                                                ),
                                                '&:hover': {
                                                    backgroundColor:
                                                        'rgba(255,255,255,0.05)',
                                                },
                                            }}
                                        >
                                            {attachment.name}
                                        </Button>
                                    ))}
                                </Box>
                            )}
                        </Box>

                        {/* Reply thread */}
                        {(replies.length > 0 || message.reply_count > 0) && (
                            <Box sx={{ marginTop: theme.spacing(3) }}>
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        color: '#8b949e',
                                        marginBottom: theme.spacing(2),
                                    }}
                                >
                                    Replies
                                </Typography>

                                {repliesLoading && replies.length === 0 && (
                                    <FlexDiv
                                        style={{
                                            justifyContent: 'center',
                                            padding: theme.spacing(2),
                                        }}
                                    >
                                        <CircularProgress
                                            size={24}
                                            style={{
                                                color: theme.palette.primary
                                                    .main,
                                            }}
                                        />
                                    </FlexDiv>
                                )}

                                {replies.map((reply) => (
                                    <ReplyBubble
                                        key={reply.id}
                                        reply={reply}
                                    />
                                ))}
                            </Box>
                        )}

                        {/* Reply composer */}
                        <Box
                            sx={{
                                marginTop: theme.spacing(3),
                                display: 'flex',
                                gap: theme.spacing(1),
                                alignItems: 'flex-end',
                            }}
                        >
                            <TextField
                                fullWidth
                                multiline
                                maxRows={4}
                                placeholder="Write a reply..."
                                value={replyText}
                                onChange={(e) => setReplyText(e.target.value)}
                                onKeyDown={handleKeyDown}
                                disabled={isSending}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: 'rgba(255,255,255,0.85)',
                                        backgroundColor:
                                            'rgba(255,255,255,0.04)',
                                        borderRadius: 2,
                                        '& fieldset': {
                                            borderColor:
                                                'rgba(255,255,255,0.15)',
                                        },
                                        '&:hover fieldset': {
                                            borderColor:
                                                'rgba(255,255,255,0.25)',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor:
                                                theme.palette.primary.main,
                                        },
                                    },
                                }}
                            />
                            <Button
                                variant="contained"
                                onClick={handleSendReply}
                                disabled={isSending || !replyText.trim()}
                                sx={{
                                    minWidth: 48,
                                    height: 48,
                                    borderRadius: 2,
                                    backgroundColor:
                                        theme.palette.primary.main,
                                    '&:hover': {
                                        backgroundColor:
                                            theme.palette.primary.dark,
                                    },
                                }}
                            >
                                {isSending ? (
                                    <CircularProgress
                                        size={20}
                                        color="inherit"
                                    />
                                ) : (
                                    <Send />
                                )}
                            </Button>
                        </Box>
                    </>
                ) : (
                    <Typography sx={{ color: 'rgba(255,255,255,0.5)' }}>
                        Message not found.
                    </Typography>
                )}
            </Box>
        </ResponsivePageContainer>
    )
}
